<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import CheckBox from 'v3/components/Form/CheckBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  experiences: {
    type: Array,
    default: () => [],
  },
  modelValue: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const selectAllRef = ref(null);

const activeExperiences = computed(() =>
  props.experiences.filter(experience => experience.active)
);

const allSelected = computed(() => {
  const ids = activeExperiences.value.map(experience => experience.id);
  return ids.length > 0 && ids.every(id => props.modelValue.includes(id));
});

const someSelected = computed(
  () => props.modelValue.length > 0 && !allSelected.value
);

watch([allSelected, someSelected], () => {
  if (selectAllRef.value) {
    selectAllRef.value.indeterminate = someSelected.value;
  }
});

const toggleAll = () => {
  if (allSelected.value) {
    emit('update:modelValue', []);
    return;
  }
  emit(
    'update:modelValue',
    activeExperiences.value.map(experience => experience.id)
  );
};

const isChecked = id => props.modelValue.includes(id);

const handleRowCheck = (experienceId, checked) => {
  const id = Number(experienceId);
  const current = [...props.modelValue];
  if (checked) {
    if (!current.includes(id)) {
      emit('update:modelValue', [...current, id]);
    }
    return;
  }
  emit(
    'update:modelValue',
    current.filter(selectedId => selectedId !== id)
  );
};
</script>

<template>
  <div class="mt-4 space-y-3">
    <p class="text-sm text-n-slate-11">
      {{ t('WIDGET_EXPERIENCES_CHECKLIST_DESCRIPTION') }}
    </p>
    <LoadingState v-if="isLoading" />
    <template v-else>
      <p
        v-if="!activeExperiences.length"
        class="text-sm text-n-slate-11 italic"
      >
        {{ t('WIDGET_EXPERIENCES_CHECKLIST_EMPTY_ACCOUNT') }}
      </p>
      <template v-else>
        <div
          class="border border-n-weak rounded-xl overflow-hidden bg-n-solid-1"
        >
          <div class="max-h-80 overflow-y-auto">
            <table class="min-w-full">
              <thead
                class="sticky top-0 z-10 bg-n-slate-2 border-b border-n-weak"
              >
                <tr>
                  <th
                    scope="col"
                    class="w-14 py-3 px-4 ltr:text-left rtl:text-right"
                  >
                    <input
                      ref="selectAllRef"
                      type="checkbox"
                      class="flex-shrink-0 border-n-strong border bg-n-slate-2 checked:border-none checked:bg-n-brand shadow-sm appearance-none rounded-[4px] w-4 h-4 focus:ring-1 after:content-[''] after:text-white checked:after:content-['✓'] after:flex after:items-center after:justify-center after:text-center after:text-xs after:font-bold after:relative cursor-pointer"
                      :checked="allSelected"
                      :aria-label="t('WIDGET_EXPERIENCES_CHECKLIST_SELECT_ALL')"
                      @change="toggleAll"
                    />
                  </th>
                  <th
                    scope="col"
                    class="py-3 ltr:pr-4 rtl:pl-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11 text-sm"
                  >
                    {{ t('WIDGET_EXPERIENCES_CHECKLIST_COLUMN_EXPERIENCE') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr
                  v-for="experience in activeExperiences"
                  :key="experience.id"
                  class="hover:bg-n-alpha-1 transition-colors"
                >
                  <td class="py-3 px-4 align-top">
                    <CheckBox
                      :value="String(experience.id)"
                      :is-checked="isChecked(experience.id)"
                      @update="handleRowCheck"
                    />
                  </td>
                  <td class="py-3 ltr:pr-4 rtl:pl-4">
                    <div class="flex items-start gap-4 min-w-0">
                      <img
                        v-if="experience.cover_image_url"
                        :src="experience.cover_image_url"
                        :alt="experience.name"
                        class="w-12 h-12 rounded-lg object-cover shrink-0"
                      />
                      <div
                        v-else
                        class="w-12 h-12 rounded-lg bg-n-alpha-2 flex items-center justify-center shrink-0"
                      >
                        <Icon
                          icon="i-lucide-sparkles"
                          class="size-4 text-n-slate-10"
                        />
                      </div>
                      <div class="flex flex-col min-w-0 gap-0.5">
                        <span
                          class="font-medium text-sm text-n-slate-12 truncate"
                        >
                          {{ experience.name }}
                        </span>
                        <span
                          v-if="experience.description"
                          class="text-sm text-n-slate-11 line-clamp-2"
                        >
                          {{ experience.description }}
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <p
          v-if="modelValue.length === 0"
          class="text-sm text-orange-600 dark:text-orange-400 italic p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800"
        >
          {{ t('WIDGET_EXPERIENCES_CHECKLIST_NONE_SELECTED') }}
        </p>
      </template>
    </template>
  </div>
</template>
